import { Dispatch, SetStateAction } from 'react'

import styled from 'styled-components'

import Box from '@totallymoney/ui/components/Box'
import Flex from '@totallymoney/ui/components/Flex'
import Heading from '@totallymoney/ui/components/Heading'
import Text from '@totallymoney/ui/components/Text'
import Icon from '@totallymoney/ui/icons/Icon'
import { IconKeys } from '@totallymoney/ui/icons/Icon/icons'
import { focusOutlineStyle } from '@totallymoney/ui/styles/focus'
import theme from '@totallymoney/ui/theme'

import {
    CreditCardProductCategory,
    ScoredCreditCardProductsCategory,
} from '@appTypes/EligibilityApi.interface'

const StyledCategoryRadioButton = styled.button<{
    isPreSelected: CategoryRadioButtonProps['isPreSelected']
    isSelected: CategoryRadioButtonProps['isSelected']
}>`
    display: flex;
    flex-direction: column;
    width: 155px;
    height: 116px;
    padding: ${theme.space20};
    margin: ${theme.space05};
    background-color: ${({ isSelected }) =>
        isSelected ? theme.grey20 : 'white'};
    border: ${({ isPreSelected, isSelected }) =>
        isPreSelected
            ? `2px solid ${theme.grey60}`
            : isSelected
              ? `2px solid ${theme.indigo100}`
              : `1px solid ${theme.grey40}`};
    border-radius: ${theme.radii.borderRadius20};
    cursor: pointer;

    &:hover,
    &:focus {
        border: ${({ isSelected }) =>
            `${isSelected ? '2px' : '1px'} solid ${theme.indigo100}`};
        ${focusOutlineStyle};
    }

    ${theme.mediaMediumPlus`
      width: 200px;
    `}

    ${theme.mediaLargePlus`
      width: 230px;
    `}
`

const iconMap: Record<CreditCardProductCategory, IconKeys> = {
    BalanceTransfer: 'balanceTransferCard',
    BestCards: 'creditCards',
    Cashback: 'cashbackCard',
    Combined: 'allRounderCard',
    Purchase: 'zeroPurchaseCard',
    Travel: 'travelCard',
    PoorCredit: 'creditBuilderCard',
    Rewards: 'rewardsCard',
}

interface CategoryRadioButtonProps {
    category: ScoredCreditCardProductsCategory
    isPreSelected: boolean
    isSelected: boolean
    setActiveCategory: Dispatch<SetStateAction<CreditCardProductCategory>>
}

const CategoryRadioButton = ({
    isPreSelected,
    isSelected,
    category,
    setActiveCategory,
}: CategoryRadioButtonProps) => (
    <StyledCategoryRadioButton
        data-testid={`${category.id}RadioButton`}
        isPreSelected={isPreSelected}
        isSelected={isSelected}
        onClick={() => setActiveCategory(category.id)}
    >
        <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            mb="space15"
        >
            <Icon icon={iconMap[category.id]} color={theme.grey100} size={32} />
            <Box
                backgroundColor={isSelected ? 'indigo60' : undefined}
                borderRadius="borderRadius20"
                borderColor={isSelected ? 'indigo60' : 'grey30'}
                borderStyle="solid"
                borderWidth={1}
                py="3px"
                px="space10"
            >
                <Text
                    variant="60"
                    weight={isSelected ? 'bold' : 'normal'}
                    color={isSelected ? 'white' : 'light'}
                >
                    {category.totalCreditCards}
                </Text>
            </Box>
        </Flex>
        <Flex alignItems="center">
            <Heading variant="20">{category.displayName}</Heading>
        </Flex>
    </StyledCategoryRadioButton>
)

export default CategoryRadioButton
